<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-container fluid>
          <v-row justify="space-between" align="center">
            <v-col cols="12" md="6">
              <h3>{{ convocatoriaData?.convocatoria || 'Convocatoria' }}</h3>
              <p class="mb-0">Personal vinculado: {{ personalVinculado.length }}</p>
            </v-col>
            <v-col cols="12" md="6">
              <v-row align="center" justify="end" class="mx-0">
                <v-col>
                  <v-text-field
                    v-model="buscaPersonal"
                    rounded
                    filled
                    dense
                    hide-details
                    background-color="primary"
                    color="secondary"
                    label="Buscar personal"
                    append-icon="mdi-magnify"
                  ></v-text-field>
                </v-col>
                <v-col cols="auto" class="pr-2">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <IconButton variant="secondary" icon="mdi-download" @click="downloadPersonalCsv" />
                      </span>
                    </template>
                    <span>Descargar CSV</span>
                  </v-tooltip>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <v-data-table
                class="primary"
                :headers="headers"
                :items="filteredPersonal"
                :loading="loading"
                loading-text="Cargando personal..."
                dense
                calculate-widths
              >
                <template v-slot:[`item.nombreCompleto`]="{ item }">
                  {{ item.nombreCompleto || '-' }}
                </template>

                <template v-slot:[`item.aceptado`]="{ item }">
                  <v-switch
                    :input-value="item.aceptado"
                    color="secondary"
                    dense
                    hide-details
                    :disabled="updating"
                    @change="mostrarConfirmacionAceptado(item, $event)"
                  ></v-switch>
                </template>

                <template v-slot:[`item.promocionado`]="{ item }">
                  <v-switch
                    :input-value="item.promocionado"
                    color="secondary"
                    dense
                    hide-details
                    :disabled="updating"
                    @change="mostrarConfirmacionPromocionado(item, $event)"
                  ></v-switch>
                </template>

                <template v-slot:[`item.acciones`]="{ item }">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <span v-bind="attrs" v-on="on">
                        <IconButton
                          class="mr-2"
                          variant="secondary"
                          elevation="8"
                          icon="mdi-delete-alert-outline"
                          @click="confirmarEliminarPersonal(item)"
                        />
                      </span>
                    </template>
                    <span>Desvincular personal de convocatoria</span>
                  </v-tooltip>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-container>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import { DIALOG_TYPES } from '@/plugins/dialog-types'
import IconButton from '@/components/base/IconButton.vue'
import downloadCsv from '@/utils/downloadCsv'

export default {
  name: 'PersonalVinculadoConvocatoriaDialogComponent',
  components: {
    IconButton,
  },

  props: {
    convocatoriaData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },

  data() {
    return {
      buscaPersonal: '',
      loading: false,
      updating: false,
      headers: [
        { text: 'CUIP', value: 'CUIP', align: 'left' },
        { text: 'Personal', value: 'nombreCompleto', align: 'left' },
        { text: 'Puesto', value: 'puesto', align: 'left' },
        { text: 'Dependencia', value: 'dependencia', align: 'left' },
        { text: 'Aceptado', value: 'aceptado', align: 'center', sortable: false },
        { text: 'Promocionado', value: 'promocionado', align: 'center', sortable: false },
        { text: 'Opciones', value: 'acciones', align: 'center', sortable: false },
      ],
    }
  },

  computed: {
    ...mapGetters('convocatorias', {
      personalVinculado: 'getPersonalPorConvocatoria',
    }),
    filteredPersonal() {
      const term = (this.buscaPersonal || '').trim().toLowerCase()
      const items = this.personalVinculado || []
      if (!term) return items
      return items.filter((p) => {
        const nombre = (p.nombreCompleto || '').toLowerCase()
        const cuip = (p.CUIP || '').toLowerCase()
        const puesto = (p.puesto || '').toLowerCase()
        const dependencia = (p.dependencia || '').toLowerCase()
        return nombre.includes(term) || cuip.includes(term) || puesto.includes(term) || dependencia.includes(term)
      })
    },
  },

  async mounted() {
    await this.loadPersonalVinculado()
  },

  methods: {
    ...mapActions('convocatorias', ['fetchPersonalByConvocatoria', 'deletePersonalConvocatoria']),
    ...mapMutations('dialog', ['SET_DIALOG']),

    async loadPersonalVinculado() {
      const idConvocatoria = this.convocatoriaData?.id

      if (!idConvocatoria) {
        console.error('No se proporcionó id de convocatoria')
        this.$store.commit('SET_SNACKBAR_MESSAGE', 'Error: No se proporcionó el ID de la convocatoria')
        return
      }

      try {
        this.loading = true
        await this.fetchPersonalByConvocatoria(idConvocatoria)
      } catch (error) {
        console.error('Error al cargar el personal vinculado:', error)
        this.$store.commit('SET_SNACKBAR_MESSAGE', 'Error al cargar el personal vinculado')
      } finally {
        this.loading = false
      }
    },

    mostrarConfirmacionAceptado(item, newValue) {
      this.updating = true
      this.SET_DIALOG({
        ...DIALOG_TYPES.CONFIRMACION_VINCULAR_PERSONAL_CONVOCATORIA,
        props: {
          item,
          tipoActualizacion: 'aceptado',
          nuevoValor: newValue,
        },
        listeners: {
          confirmed: () => {
            this.updating = false
            this.loadPersonalVinculado()
          },
          cancelled: () => {
            this.updating = false
            this.loadPersonalVinculado()
          },
          error: () => {
            this.updating = false
            this.loadPersonalVinculado()
          },
        },
      })
    },

    mostrarConfirmacionPromocionado(item, newValue) {
      this.updating = true
      this.SET_DIALOG({
        ...DIALOG_TYPES.CONFIRMACION_VINCULAR_PERSONAL_CONVOCATORIA,
        props: {
          item,
          tipoActualizacion: 'promocionado',
          nuevoValor: newValue,
        },
        listeners: {
          confirmed: () => {
            this.updating = false
            this.loadPersonalVinculado()
          },
          cancelled: () => {
            this.updating = false
            this.loadPersonalVinculado()
          },
          error: () => {
            this.updating = false
            this.loadPersonalVinculado()
          },
        },
      })
    },

    confirmarEliminarPersonal(item) {
      this.updating = true
      this.SET_DIALOG({
        ...DIALOG_TYPES.CONFIRMACION_ELIMINAR_PERSONAL_CONVOCATORIA,
        props: {
          personalData: item,
        },
        listeners: {
          confirmed: () => {
            this.eliminarPersonalConvocatoria(item)
          },
          cancelled: () => {
            this.updating = false
          },
        },
      })
    },

    async eliminarPersonalConvocatoria(item) {
      try {
        await this.deletePersonalConvocatoria({
          idPersonal: item.id_personal,
          idConvocatoriaPersonal: item.idConvocatoriaPersonal,
        })

        await this.loadPersonalVinculado()
      } catch (error) {
        console.error('Error al eliminar la asignación:', error)
        this.$store.commit('SET_SNACKBAR_MESSAGE', 'Error al eliminar la asignación de personal')
      } finally {
        this.updating = false
      }
    },

    downloadPersonalCsv() {
      const convName = this.convocatoriaData?.convocatoria || 'Convocatoria'
      const rows = (this.filteredPersonal || []).map((item) => ({
        Convocatoria: convName,
        CUIP: item.CUIP || '',
        Personal: item.nombreCompleto || '',
        Puesto: item.puesto || '',
        Dependencia: item.dependencia || '',
        Aceptado: item.aceptado ? 'Sí' : 'No',
        Promocionado: item.promocionado ? 'Sí' : 'No',
      }))

      const filename = `personal-vinculado-${convName}`
      downloadCsv(rows, { filename })
    },
  },
}
</script>
